/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useMemo } from 'react';

import Lottie from 'lottie-react';

import { css, jsx } from '@atlaskit/css';
import { useIsReducedMotion } from '@atlaskit/motion/use-is-reduced-motion';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import remixAnimationCardPurple from './RemixPlaceholder/assets/remix-animation-card-purple.png';
import remixAnimation from './RemixPlaceholder/assets/remix-animation.json';
import { layerTokens } from './RemixPlaceholder/layerTokens';

const REMIX_ANIMATION_IMAGE_ASSET_ID = 'card_purple_1_1';

type LottieColor = [number, number, number, number];

type LottieNode = {
	[key: string]: unknown;
	c?: { a?: number; k?: unknown };
	g?: { k?: { k?: unknown }; p?: number };
	nm?: string;
	refId?: string;
	t?: {
		d?: {
			k?: Array<{ s?: { fc?: unknown } }>;
		};
	};
	ty?: number | string;
};

type LayerColor = {
	color: string;
	opacity?: number;
};

/** Converts a resolved hex token value into the normalized RGBA format expected by Lottie. */
const hexToLottieColor = (color: string, opacity = 1): LottieColor => {
	const hex = color.replace('#', '');
	const red = Number.parseInt(hex.slice(0, 2), 16);
	const green = Number.parseInt(hex.slice(2, 4), 16);
	const blue = Number.parseInt(hex.slice(4, 6), 16);

	return [red / 255, green / 255, blue / 255, opacity];
};

/** Recolors every RGB stop in a Lottie gradient while preserving its stop positions. */
const replaceGradientColorStops = (gradient: unknown, color: LottieColor): unknown => {
	if (!gradient || typeof gradient !== 'object') {
		return gradient;
	}

	const { k: animatedGradient, p: colorStopCount } = gradient as NonNullable<LottieNode['g']>;
	if (!animatedGradient || !Array.isArray(animatedGradient.k) || !colorStopCount) {
		return gradient;
	}

	const colorStops = [...animatedGradient.k];
	for (let index = 0; index < colorStopCount; index++) {
		const colorOffset = index * 4;
		colorStops[colorOffset + 1] = color[0];
		colorStops[colorOffset + 2] = color[1];
		colorStops[colorOffset + 3] = color[2];
	}

	return { ...gradient, k: { ...animatedGradient, k: colorStops } };
};

/** Recolors the font color in every keyframe of a Lottie text document. */
const replaceTextColor = (text: unknown, color: LottieColor): unknown => {
	if (!text || typeof text !== 'object') {
		return text;
	}

	const { d: textDocument } = text as NonNullable<LottieNode['t']>;
	if (!textDocument?.k) {
		return text;
	}

	return {
		...text,
		d: {
			...textDocument,
			k: textDocument.k.map((keyframe) => ({
				...keyframe,
				s: keyframe.s ? { ...keyframe.s, fc: color } : keyframe.s,
			})),
		},
	};
};

/** Recursively recolors fills, gradients, and text contained within a selected Lottie layer. */
const replaceFillColors = (value: unknown, color: LottieColor): unknown => {
	if (Array.isArray(value)) {
		return value.map((item) => replaceFillColors(item, color));
	}

	if (!value || typeof value !== 'object') {
		return value;
	}

	const node = value as LottieNode;
	return Object.fromEntries(
		Object.entries(node).map(([key, item]) => {
			if (node.ty === 'fl' && key === 'c' && item && typeof item === 'object') {
				return [key, { ...item, k: color }];
			}
			if ((node.ty === 'gf' || node.ty === 'gs') && key === 'g') {
				return [key, replaceGradientColorStops(item, color)];
			}
			if (node.ty === 5 && key === 't') {
				return [key, replaceTextColor(item, color)];
			}

			return [key, replaceFillColors(item, color)];
		}),
	);
};

/** Resolves the static layer-to-token map against the currently active token theme. */
const getLayerColors = (): Record<string, LayerColor> => {
	const layerColors: Record<string, LayerColor> = {};

	Object.entries(layerTokens).forEach(([layerName, { color: getColor, opacity }]) => {
		const color = getColor();
		if (color) {
			layerColors[layerName] = { color, opacity };
		}
	});

	return layerColors;
};

/** Creates a themed copy of the animation and replaces its embedded image with the local asset. */
const createRemixAnimationData = (_colorMode: unknown) => {
	const layerColors = getLayerColors();
	const referencedAssetColors = Object.fromEntries(
		remixAnimation.layers.flatMap((layer) => {
			const layerColor = layerColors[layer.nm];
			return layer.refId && layerColor ? [[layer.refId, layerColor]] : [];
		}),
	);

	return {
		...remixAnimation,
		layers: remixAnimation.layers.map((layer) => {
			const layerColor = layerColors[layer.nm];
			return layerColor
				? (replaceFillColors(
						layer,
						hexToLottieColor(layerColor.color, layerColor.opacity),
					) as typeof layer)
				: layer;
		}),
		assets: remixAnimation.assets.map((asset) => {
			if (asset.id === REMIX_ANIMATION_IMAGE_ASSET_ID) {
				return { ...asset, p: remixAnimationCardPurple, u: '' };
			}

			const assetColor = referencedAssetColors[asset.id];
			return assetColor
				? (replaceFillColors(
						asset,
						hexToLottieColor(assetColor.color, assetColor.opacity),
					) as typeof asset)
				: asset;
		}),
	};
};

const placeholderStyles = css({
	width: '100%',
	maxWidth: '760px',
	aspectRatio: '16 / 9',
	borderRadius: token('radius.xlarge'),
	borderWidth: token('border.width'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	marginRight: 'auto',
	marginLeft: 'auto',
	overflow: 'hidden',
	pointerEvents: 'none',
});

const rendererSettings = { preserveAspectRatio: 'xMidYMid meet' };

/** Marks where an accepted SUGGESTED_REMIX result will land while its card is active. */
export function RemixPlaceholder(): JSX.Element {
	const isReducedMotion = useIsReducedMotion();
	const { colorMode } = useThemeObserver();
	const animationData = useMemo(() => createRemixAnimationData(colorMode), [colorMode]);

	return (
		<Lottie
			key={`${colorMode}-${isReducedMotion ? 'reduced-motion' : 'animated'}`}
			animationData={animationData}
			autoplay={!isReducedMotion}
			loop={false}
			rendererSettings={rendererSettings}
			css={placeholderStyles}
			aria-hidden="true"
			data-testid="ai-suggestions-remix-placeholder"
		/>
	);
}
